import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import {
  Edit,
  Trash2,
  ArrowLeft,
  User,
  Mail,
  Phone,
  PhoneOff,
  Building,
  Globe,
  MapPin,
  FileText,
  Tag,
  DollarSign,
  Calendar,
  Eye,
  Send,
  X,
  MessageSquare,
  Printer,
  Download,
  FileIcon,
  Instagram,
  Facebook,
  UserCheck,
  Inbox,
  Paperclip,
  Upload,
  Image as ImageIcon,
  AlertTriangle
} from 'lucide-react';
import api, { leadsAPI, organizationsAPI, opendentalAPI } from '../services/api';
import {
  formatName,
  formatEmail,
  formatPhone,
  formatCompany,
  formatDate,
  formatDateTime,
  formatCurrency,
  formatAddress,
  formatWebsite,
  getStatusText,
  getPriorityText,
  getSourceText,
  getStatusColor,
  getPriorityColor,
  getNoteSourceText,
  getNoteSourceColor,
  getNoteSourceIcon
} from '../utils/formatters';
import toast from 'react-hot-toast';
import ConfirmationModal from '../components/ConfirmationModal';
import MetaLeadFormCard, { FORM_NOTE_SOURCES, looksLikeFormSubmission } from '../components/MetaLeadFormCard';
import CallTranscriptPanel from '../components/CallTranscriptPanel';
import VoipRecordingPlayer from '../components/VoipRecordingPlayer';
import BrowserCallButton from '../components/BrowserCallButton';
import useLeadClaim from '../hooks/useLeadClaim';
import SettingsRequiredModal from '../components/SettingsRequiredModal';
import LeadTasksWidget from '../components/LeadTasksWidget';
import LeadFollowUpWidget from '../components/LeadFollowUpWidget';
import LeadRecentMessages from '../components/LeadRecentMessages';
import LeadActivityTimeline from '../components/LeadActivityTimeline';
import PhoneAgentLiveCall from '../components/PhoneAgentLiveCall';
import CustomFieldsDisplay from '../components/CustomFieldsDisplay';
import CustomFieldsInput from '../components/CustomFieldsInput';
import { useAuth } from '../contexts/AuthContext';
import OpenDentalSyncBadge from '../components/OpenDentalSyncBadge';
import AdAttributionBadge from '../components/AdAttributionBadge';
import { useOpenDentalBooking } from '../contexts/OpenDentalBookingContext';
import LeadAvatar from '../components/LeadAvatar';
import LeadPhoneNumbers, { leadNumbers } from '../components/LeadPhoneNumbers';
import ReengagedNotice from '../components/ReengagedNotice';
import { smsPartCount } from '../utils/sms';

// Environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

/**
 * Convert URLs in text to clickable links (skip URLs already inside HTML tags)
 */
function linkifyText(text) {
  if (!text) return '';
  
  // URL'leri linke çevir (HTML etiketlerinin dışındakileri)
  const linkified = text.replace(/(<[^>]*>)|(https?:\/\/[^\s<]+)/g, (match, htmlTag, url) => {
    if (htmlTag) return htmlTag; // Mevcut HTML etiketlerini koru
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary-600 hover:text-primary-700 underline">${url}</a>`;
  });

  // DOMPurify ile temizle (Sistem etiketlerine ve stillerine izin ver)
  return DOMPurify.sanitize(linkified, {
    ALLOWED_TAGS: ['div', 'span', 'h1', 'h2', 'h3', 'p', 'br', 'a', 'b', 'i', 'strong', 'em', 'audio', 'source', 'img'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'src', 'controls', 'type', 'width', 'height']
  });
}

/**
 * Format string as price input (allow only numbers and one dot)
 */
const formatPriceInput = (value) => {
  if (!value) return '';
  
  // Önce sadece rakamları ve noktayı al
  let cleaned = value.replace(/[^0-9.]/g, '');
  
  // Birden fazla nokta varsa temizle
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    cleaned = parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Integer ve Decimal kısımlarını ayır
  let integerPart = parts[0];
  let decimalPart = parts.length === 2 ? '.' + parts[1].substring(0, 2) : '';

  // Integer kısmına binlik ayraç ekle (100000 -> 100,000)
  if (integerPart) {
    integerPart = parseInt(integerPart, 10).toLocaleString('en-US');
  } else if (parts[0] === '') {
    integerPart = '0';
  }

  // Eğer giriş sadece nokta ile başlıyorsa (.55 gibi)
  if (value.startsWith('.')) return '0' + decimalPart;

  return integerPart + decimalPart;
};

/**
 * Format string as odometer input (allow only numbers, add commas)
 */
const formatOdometerInput = (value) => {
  if (!value) return '';
  // Sadece rakamları al
  let cleaned = value.replace(/[^0-9]/g, '');
  // Binlik ayraç ekle
  if (cleaned) {
    return parseInt(cleaned, 10).toLocaleString('en-US');
  }
  return '';
};

const DNC_CHANNEL_LABELS = {
  sms: 'SMS', whatsapp: 'WhatsApp', email: 'email', messenger: 'Messenger', instagram: 'Instagram',
  meta_ad: 'a Meta lead form', google_ad: 'a Google Ads form', tiktok_ad: 'a TikTok form', webform: 'a website form',
  phone: 'a phone call', phone_agent: 'a phone call',
};

// Renders clickable social profile links pulled from the lead's customFields.
// Inbound capture writes instagram_username (and tags meta:psid:/meta:igsid:)
// when DMs come in, so this surfaces the most useful one — IG profile URL —
// right in the header.
const SocialLinks = ({ lead }) => {
  const cf = lead?.customFields instanceof Map
    ? Object.fromEntries(lead.customFields)
    : (lead?.customFields || {});
  const igUsername = cf.instagram_username;
  const tags = lead?.tags || [];
  const hasMessenger = tags.some((t) => typeof t === 'string' && t.startsWith('meta:psid:'));
  const hasInstagram = !!igUsername || tags.some((t) => typeof t === 'string' && t.startsWith('meta:igsid:'));
  if (!hasMessenger && !hasInstagram) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2 text-xs">
      {igUsername && (
        <a
          href={`https://instagram.com/${igUsername}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-medium hover:opacity-90 transition"
        >
          <Instagram size={12} /> @{igUsername}
        </a>
      )}
      {hasInstagram && !igUsername && (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-pink-100 text-pink-800 font-medium">
          <Instagram size={12} /> Instagram conversation
        </span>
      )}
      {hasMessenger && (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 font-medium">
          <Facebook size={12} /> Messenger conversation
        </span>
      )}
    </div>
  );
};

// Authenticated thumbnail loader — attachments live behind the API, so
// a plain <img src> can't carry the auth header; fetch the blob and
// swap in an object URL. Falls back to the generic image icon while
// loading or when the server has no thumbnail (undecodable file).
// Object URLs are kept for the session (react-query caches the URL, so
// revoking on unmount would break re-renders served from cache).
const AttachmentThumb = ({ leadId, attachment, className }) => {
  const { data: thumbUrl } = useQuery(
    ['attachment-thumb', leadId, attachment._id],
    async () => {
      const res = await leadsAPI.getAttachmentThumbnail(leadId, attachment._id);
      return window.URL.createObjectURL(new Blob([res.data], { type: 'image/jpeg' }));
    },
    { staleTime: Infinity, retry: false }
  );
  if (!thumbUrl) return <ImageIcon size={16} className="text-blue-600" />;
  return <img src={thumbUrl} alt={attachment.originalName} className={className} />;
};

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Aliased — the queries below also bind a local named `organization`
  // for the legacy /api/leads/getOrganization payload. Don't merge them
  // without checking every usage; the shapes differ.
  const { user: currentUser, organization: authOrg } = useAuth();
  const { interceptWonTransition, openBookingForLead, isOpenDentalReady } = useOpenDentalBooking();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    subject: '',
    message: '',
    emailType: 'custom',
    attachments: []
  });
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [showSMSModal, setShowSMSModal] = useState(false);
  // "Remove from Do Not Call" — reason required, kept on the lead + timeline.
  const [showDncClear, setShowDncClear] = useState(false);
  const [dncReason, setDncReason] = useState('');
  const [smsData, setSmsData] = useState({
    message: '',
    phoneField: 'phone',
    topic: ''
  });
  const [isGeneratingSMSAI, setIsGeneratingSMSAI] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [showBusinessCardModal, setShowBusinessCardModal] = useState(false);
  const [notesPage, setNotesPage] = useState(1);
  const [notesPerPage, setNotesPerPage] = useState(10);
  const [timelineFilter, setTimelineFilter] = useState('all');
  const [noteCustomFields, setNoteCustomFields] = useState({});

  const { data: noteCustomFieldDefs = [] } = useQuery(
    ['custom-fields', 'note'],
    () => api.get('/custom-fields', { params: { entity: 'note' } }).then((r) => r.data),
    { staleTime: 60 * 1000 }
  );
  const [showServiceRecordModal, setShowServiceRecordModal] = useState(false);
  const [showCreateServiceRecordModal, setShowCreateServiceRecordModal] = useState(false);
  const [selectedServiceRecord, setSelectedServiceRecord] = useState(null);
  const [serviceRecordForm, setServiceRecordForm] = useState({
    vehicleModel: '',
    odometer: '',
    extraInfo: '',
    remarks: '',
    items: [{ description: '', category: '', status: 'PASSED', price: '' }]
  });

  // Confirmation Modal State
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    actionType: null, // 'delete_lead' or 'delete_note'
    data: null // id or noteId
  });

  // Settings-required warning modal (shown when SMTP/SMS not configured)
  const [settingsRequiredModal, setSettingsRequiredModal] = useState({
    isOpen: false,
    type: 'email' // 'email' | 'sms'
  });

  // SMTP configuration status
  const { data: smtpSettings } = useQuery(
    ['smtp-settings'],
    () => api.get('/smtp/settings').then((res) => res.data),
    {
      staleTime: 60 * 1000,
      retry: false
    }
  );

  // SMS lives on the VoIP.ms integration now (ClickSend was retired).
  // Use the lightweight /voip/status endpoint instead of /voip/settings
  // so non-admin reps (no manage_smtp_settings permission) can still
  // read whether SMS is wired up without a 403 freezing the page.
  const { data: smsSettings } = useQuery(
    ['voip-status'],
    () => api.get('/voip/status').then((res) => res.data),
    {
      staleTime: 60 * 1000,
      retry: false
    }
  );

  const isSmtpConfigured = !!smtpSettings?.isConfigured;
  const isSmsConfigured = smsSettings?.provider === 'voipms' && !!smsSettings?.isConfigured;
  const canManageSettings = currentUser?.permissions?.manage_smtp_settings !== false;

  // Organization bilgilerini çek
  const { data: organization } = useQuery(
    ['organization', currentUser?.organization],
    () => leadsAPI.getOrganization(currentUser?.organization),
    {
      enabled: !!currentUser?.organization,
      staleTime: 5 * 60 * 1000, // 5 dakika
    }
  );

  // Organization bilgilerini kullan
  const SENDER_NAME = organization?.data?.organization?.name || 'CRM System';
  const COMPANY_NAME = organization?.data?.organization?.name || 'Your Company';
  const COMPANY_WEBSITE = organization?.data?.organization?.domain ? `https://${organization?.data?.organization?.domain}` : 'https://yourcompany.com';

  // SSE bağlantısı
  useEffect(() => {
    const _token = localStorage.getItem('token');
    if (!_token) return;
    const eventSource = new EventSource(`${API_BASE_URL}/events?token=${encodeURIComponent(_token)}`);

    eventSource.onopen = () => {
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Note eklendiğinde veya silindiğinde lead verilerini güncelle
        if (data.type === 'note_added' && data.leadId === id) {
          queryClient.invalidateQueries(['lead', id]);
        } else if (data.type === 'note_deleted' && data.leadId === id) {
          queryClient.invalidateQueries(['lead', id]);
        } else if (data.type === 'conversation_updated' && String(data.leadId) === id) {
          // A new SMS / email / DM: refresh the Recent messages card, and
          // the follow-up card (a reply ends the sequence).
          queryClient.invalidateQueries(['lead-recent-messages', id]);
          queryClient.invalidateQueries(['lead-follow-up', id]);
        }
      } catch (error) {
        console.error('❌ SSE parsing error:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('❌ SSE connection error:', error);
    };

    // Component unmount olduğunda bağlantıyı kapat
    return () => {
      eventSource.close();
    };
  }, [id, queryClient]);

  const { data: lead, isLoading, error } = useQuery(
    ['lead', id],
    () => leadsAPI.getLead(id),
    {
      refetchOnWindowFocus: true,
      staleTime: 0
    }
  );

  // First-come-first-served — clicking the phone number claims the lead.
  // Hook must run on every render (Rules of Hooks); the hook itself
  // gracefully handles `lead` being undefined during the initial fetch.
  const claim = useLeadClaim({ lead: lead?.data || lead, currentUser });

  const { data: orgUsersResp } = useQuery(
    ['organization', 'users'],
    () => organizationsAPI.getUsers(),
    { staleTime: 60_000 }
  );
  const orgUsers = orgUsersResp?.data?.users || [];

  // Org's pipeline stages — drives the editable Stage dropdown that
  // replaces the hardcoded Status enum on this page. Stage names + colours
  // come straight from Pipeline Settings so renames there flow here.
  const { data: stagesData } = useQuery(
    ['pipeline-stages'],
    async () => {
      const pipelines = await api.get('/pipelines').then((r) => r.data);
      if (!pipelines?.[0]) return [];
      return api.get(`/pipelines/${pipelines[0]._id}/stages`).then((r) => r.data);
    },
    { staleTime: 60_000 }
  );
  const stages = stagesData || [];

  // Auto-repair UI is now gated at the org level (Organization.autoRepair.enabled)
  // managed in SuperAdmin → Add-ons. Was previously a per-role permission;
  // see the migration in server/index.js for the cutover.
  const hasAutoRepairPermission = !!authOrg?.autoRepair?.enabled;

  const notesLength = lead?.data?.notes?.length ?? lead?.notes?.length ?? 0;

  // Lead değişince not sayfalamasını başa al
  useEffect(() => {
    setNotesPage(1);
  }, [id]);

  // Not sayısı / sayfa boyutu değişince sayfayı güvenli aralığa çek
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(notesLength / notesPerPage));
    setNotesPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [notesLength, notesPerPage]);

  const deleteMutation = useMutation(leadsAPI.deleteLead, {
    onSuccess: () => {
      toast.success('Lead deleted successfully!');
      queryClient.invalidateQueries(['leads']);
      queryClient.invalidateQueries('dashboardStats');
      navigate('/leads');
    },
    onError: (error) => {
      const message = error.response?.data?.error || 'An error occurred while deleting the lead';
      toast.error(message);
    }
  });

  // ── Lead attachments ────────────────────────────────────────────
  // Product spec: max 10 files per selection, 10 MB per file. Images
  // are recompressed server-side (lossless for PNG/WebP, visually
  // lossless mozjpeg for JPEG).
  const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024; // 10 MB
  const MAX_ATTACHMENTS_PER_UPLOAD = 10;

  const uploadAttachmentsMutation = useMutation(
    (files) => leadsAPI.uploadAttachments(id, files),
    {
      onSuccess: (_res, files) => {
        toast.success(`${files.length} file${files.length > 1 ? 's' : ''} uploaded`);
        queryClient.invalidateQueries(['lead', id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'File upload failed');
      }
    }
  );

  const deleteAttachmentMutation = useMutation(
    (attachmentId) => leadsAPI.deleteAttachment(id, attachmentId),
    {
      onSuccess: () => {
        toast.success('Attachment deleted');
        queryClient.invalidateQueries(['lead', id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Could not delete attachment');
      }
    }
  );

  const handleAttachmentSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // reset so the same file can be re-picked later
    if (!files.length) return;
    if (files.length > MAX_ATTACHMENTS_PER_UPLOAD) {
      toast.error('You can upload at most 10 files at once');
      return;
    }
    const oversized = files.filter((f) => f.size > MAX_ATTACHMENT_SIZE);
    if (oversized.length) {
      toast.error(`Over the 10 MB limit: ${oversized.map((f) => f.name).join(', ')}`);
    }
    const valid = files.filter((f) => f.size <= MAX_ATTACHMENT_SIZE);
    if (valid.length) uploadAttachmentsMutation.mutate(valid);
  };

  // Download goes through the authenticated API (blob), not a static
  // URL — attachments are private, so a plain <a href> can't carry the
  // auth header.
  const handleDownloadAttachment = async (att) => {
    try {
      const res = await leadsAPI.downloadAttachment(id, att._id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = att.originalName || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_) {
      toast.error('Could not download file');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Preview lightbox — images render inline, PDFs go through an iframe;
  // anything else falls back to a plain download.
  const [attachmentPreview, setAttachmentPreview] = useState({
    isOpen: false, att: null, url: null, loading: false
  });

  const isPreviewableAttachment = (att) =>
    (att.mimeType || '').startsWith('image/') || att.mimeType === 'application/pdf';

  const openAttachmentPreview = async (att) => {
    if (!isPreviewableAttachment(att)) {
      handleDownloadAttachment(att);
      return;
    }
    setAttachmentPreview({ isOpen: true, att, url: null, loading: true });
    try {
      const res = await leadsAPI.downloadAttachment(id, att._id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: att.mimeType }));
      setAttachmentPreview({ isOpen: true, att, url, loading: false });
    } catch (_) {
      toast.error('Could not load preview');
      setAttachmentPreview({ isOpen: false, att: null, url: null, loading: false });
    }
  };

  const closeAttachmentPreview = () => {
    setAttachmentPreview((prev) => {
      if (prev.url) window.URL.revokeObjectURL(prev.url);
      return { isOpen: false, att: null, url: null, loading: false };
    });
  };

  const sendEmailMutation = useMutation(
    (data) => leadsAPI.sendEmail(id, data),
    {
      onSuccess: () => {
        toast.success('Email sent successfully!');
        setShowEmailModal(false);
        setEmailData({ subject: '', message: '', emailType: 'custom', attachments: [] });
        queryClient.invalidateQueries(['lead', id]);
        queryClient.invalidateQueries('dashboardStats');
      },
      onError: (error) => {
        const errorData = error.response?.data;

        // Unsubscribe hatası için özel mesaj
        if (errorData?.details?.legalWarning) {
          const details = errorData.details;
          const errorMessage = `🚫 ${errorData.error}\n\n📅 ${details.message}\n⚠️ ${details.legalWarning}`;
          toast.error(errorMessage, {
            duration: 6000,
            style: {
              background: '#000000',
              border: '1px solid #333333',
              color: '#ffffff'
            }
          });
        } else {
          const message = errorData?.error || 'An error occurred while sending email';
          toast.error(message);
        }
      }
    }
  );

  const clearDncMutation = useMutation(
    (reason) => leadsAPI.clearDnc(id, reason),
    {
      onSuccess: () => {
        toast.success('Removed from Do Not Call');
        setShowDncClear(false);
        setDncReason('');
        queryClient.invalidateQueries(['lead', id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Could not remove from Do Not Call');
      },
    }
  );

  const sendSMSMutation = useMutation(
    (data) => leadsAPI.sendSMS(id, data),
    {
      onSuccess: () => {
        toast.success('SMS sent successfully!');
        setShowSMSModal(false);
        setSmsData({ message: '', phoneField: 'phone', topic: '' });
        queryClient.invalidateQueries(['lead', id]);
        queryClient.invalidateQueries('dashboardStats');
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || 'An error occurred while sending SMS';
        const errorDetails = error.response?.data?.details || '';
        toast.error(`${errorMessage}${errorDetails ? '\n' + errorDetails : ''}`);
      }
    }
  );

  const addNoteMutation = useMutation(
    ({ content, customFields }) => leadsAPI.addNote(id, content, customFields),
    {
      onSuccess: () => {
        toast.success('Note added successfully!');
        setNewNote('');
        setNoteCustomFields({});
        setNotesPage(1);
        // setShowNoteForm(false); // Form'u açık tut
        queryClient.invalidateQueries(['lead', id]);
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'An error occurred while adding note';
        toast.error(message);
      }
    }
  );

  const deleteNoteMutation = useMutation(
    (noteId) => leadsAPI.deleteNote(id, noteId),
    {
      onSuccess: () => {
        toast.success('Note deleted successfully!');
        queryClient.invalidateQueries(['lead', id]);
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'An error occurred while deleting note';
        toast.error(message);
      }
    }
  );

  const updateLeadMutation = useMutation(
    (data) => leadsAPI.updateLead(id, data),
    {
      onSuccess: () => {
        toast.success('Lead updated successfully!');
        queryClient.invalidateQueries(['lead', id]);
        queryClient.invalidateQueries(['leads']);
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'An error occurred while updating the lead';
        toast.error(message);
      }
    }
  );

  const handleStatusChange = (newStatus) => {
    updateLeadMutation.mutate({ status: newStatus });
  };

  // Manual "Sync now" for a lead stuck pending/failed (OD was down at win
  // time, or an automation Won). Replays the stored booking intent instead
  // of waiting for the background retry cron.
  const retrySyncMutation = useMutation(
    () => opendentalAPI.retrySync(id),
    {
      onSuccess: (res) => {
        const d = res?.data || {};
        if (d.synced) toast.success(`Synced to OpenDental (appointment #${d.aptNum})`);
        else if (d.skipped) toast(d.skipped);
        else toast.success('Sync attempted');
        queryClient.invalidateQueries(['lead', id]);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Sync failed'),
    }
  );

  const handleStageChange = async (newStageId) => {
    // OpenDental: moving into a Won stage books an appointment first.
    // Resolves 'proceed' immediately unless the add-on is configured and
    // this is a fresh Won transition for an unsynced lead.
    const targetStage = (stages || []).find((s) => String(s._id) === String(newStageId));
    if (targetStage?.closedType === 'won') {
      const decision = await interceptWonTransition({ lead: leadData, targetStage });
      if (decision === 'cancelled') return;
    }
    updateLeadMutation.mutate({ stageId: newStageId });
  };

  const handlePriorityChange = (newPriority) => {
    updateLeadMutation.mutate({ priority: newPriority });
  };

  const handleAssignChange = (userId) => {
    updateLeadMutation.mutate({ assignedTo: userId || null });
  };





  const handleSendEmail = async () => {
    if (!emailData.subject.trim() || !emailData.message.trim()) {
      toast.error('Subject and message fields are required');
      return;
    }

    // Dosya boyutu kontrolü (toplam 10MB)
    const totalSize = emailData.attachments.reduce((total, file) => total + file.size, 0);
    if (totalSize > 10 * 1024 * 1024) {
      toast.error('Total file size must be less than 10MB');
      return;
    }

    try {

      if (emailData.attachments.length === 0) {
        sendEmailMutation.mutate({ ...emailData, recipientEmail: selectedEmail || leadData.email });
        return;
      }

      // Attachment'ları base64'e çevir
      const processedAttachments = await Promise.all(
        emailData.attachments.map(async (file) => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64Content = reader.result.split(',')[1];
              resolve({
                name: file.name,
                type: file.type,
                content: base64Content,
                size: file.size
              });
            };
            reader.readAsDataURL(file);
          });
        })
      );


      // Email data'yı hazırla
      const emailDataWithAttachments = {
        ...emailData,
        attachments: processedAttachments,
        recipientEmail: selectedEmail || leadData.email
      };

      sendEmailMutation.mutate(emailDataWithAttachments);
    } catch (error) {
      console.error('Error processing attachments:', error);
      toast.error('Error processing attachments');
    }
  };

  const handleEmailButtonClick = () => {
    if (!isSmtpConfigured) {
      setSettingsRequiredModal({ isOpen: true, type: 'email' });
      return;
    }
    // FCFS — opening the compose modal is high-intent; route through
    // the claim guard so non-admins can't draft into someone else's
    // lead, and unassigned leads get claimed before the modal opens.
    if (claim.ownerSelf) { setShowEmailModal(true); return; }
    claim.requestClaim(() => setShowEmailModal(true));
  };

  const handleSendSMS = () => {
    if (!smsData.message.trim()) {
      toast.error('Message is required');
      return;
    }
    sendSMSMutation.mutate(smsData);
  };

  const handleSMSButtonClick = () => {
    if (!isSmsConfigured) {
      setSettingsRequiredModal({ isOpen: true, type: 'sms' });
      return;
    }
    const openModal = () => {
      setSmsData({ message: '', phoneField: 'phone', topic: '' });
      setShowSMSModal(true);
    };
    // FCFS claim guard — same as the email button above.
    if (claim.ownerSelf) { openModal(); return; }
    claim.requestClaim(openModal);
  };

  const handleConfirmAction = () => {
    if (confirmationModal.actionType === 'delete_lead') {
      deleteMutation.mutate(id);
    } else if (confirmationModal.actionType === 'delete_note') {
      deleteNoteMutation.mutate(confirmationModal.data);
    } else if (confirmationModal.actionType === 'delete_attachment') {
      deleteAttachmentMutation.mutate(confirmationModal.data);
    }
  };

  const handleDeleteAttachment = (att) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Attachment',
      message: `Are you sure you want to delete "${att.originalName}"? This action cannot be undone.`,
      actionType: 'delete_attachment',
      data: att._id
    });
  };

  const handleDelete = () => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Lead',
      message: 'Are you sure you want to delete this lead? This action cannot be undone.',
      actionType: 'delete_lead',
      data: id
    });
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;
    claim.requestClaim(() => {
      const cf = Object.keys(noteCustomFields).length ? noteCustomFields : null;
      addNoteMutation.mutate({ content: newNote.trim(), customFields: cf });
    });
  };

  const handleDeleteNote = (noteId) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Note',
      message: 'Are you sure you want to delete this note?',
      actionType: 'delete_note',
      data: noteId
    });
  };

  const handlePrintSingleNote = (note) => {
    const printWindow = window.open('', '_blank');
    
    // Sadece not içeriğini oluştur (başlık, tarih vb. her şeyi kaldır)
    const noteHtml = `
      <div style="font-size: 16px; line-height: 1.6; color: #000; white-space: pre-wrap;">
        ${linkifyText(note.content)}
      </div>
    `;

    printWindow.document.write(`
      <html>
        <head>
          <title>Note Print</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; padding: 20px; color: #000; }
            .note-content a { color: #2563eb; text-decoration: underline; }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <div>
            ${noteHtml}
          </div>
          <script>
            window.onload = function() { 
              setTimeout(function() { 
                window.print(); 
                window.onafterprint = function() { window.close(); };
              }, 500); 
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Close modal on mutation success (can be handled in onSuccess too, but checking here)
  useEffect(() => {
    if (deleteMutation.isSuccess || deleteNoteMutation.isSuccess || deleteAttachmentMutation.isSuccess) {
      setConfirmationModal(prev => ({ ...prev, isOpen: false }));
    }
  }, [deleteMutation.isSuccess, deleteNoteMutation.isSuccess, deleteAttachmentMutation.isSuccess]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Loading lead...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">
          <Eye size={48} className="mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Lead Not Found</h3>
        <p className="text-gray-600">The lead you are looking for was not found or may have been deleted.</p>
        <Link to="/leads" className="btn-primary mt-4">
          Back to Lead List
        </Link>
      </div>
    );
  }

  if (!lead) {
    return null;
  }

  // API response'u kontrol et
  const leadData = lead.data || lead;

  // Lead Detail surfaces two kinds of entries:
  //   - operator memos          (source = web)
  //   - one-shot form snapshots (webform / meta_ad / google_ad / tiktok_ad)
  //   - appointments            (source = appointment)
  // Ongoing conversations (email / sms / phone / messenger / instagram /
  // whatsapp) are excluded — those are the Inbox's job and would
  // double up here. Form submissions ARE shown because they're the
  // original lead data the customer handed us, not a dialog.
  //
  // Exception: Click-to-Messenger Lead Ads embed the form contents in
  // the very first DM, and Meta sometimes never fires a separate
  // leadgen webhook for them. That leaves the lead with only a
  // messenger / instagram note whose body IS the form recap. Let those
  // through if their shape matches a form submission so the lead's
  // form data still surfaces here.
  // 'phone' is allowed here so VoIP calls render in the Notes timeline
  // (green card + inline audio player when a recording exists). Email
  // and SMS still belong to the Inbox to avoid double-up, but calls are
  // a per-lead artifact rather than an open conversation thread.
  const LEAD_DETAIL_SOURCES = new Set([
    'web', 'appointment', 'webform', 'meta_ad', 'google_ad', 'tiktok_ad', 'phone', 'phone_agent',
  ]);
  const AD_FORM_SOURCES = new Set(['webform', 'meta_ad', 'google_ad', 'tiktok_ad']);
  const FORM_FALLBACK_SOURCES = new Set(['messenger', 'instagram']);
  // Click-to-Messenger lead form cards need the leadgen reference id
  // from the lead's tag (their content has no `leadgen_id:` line).
  const leadgenIdFromTag = (leadData?.tags || [])
    .find((t) => typeof t === 'string' && /^meta:leadgen:/.test(t))
    ?.split(':')[2] || null;
  const rawNotes = Array.isArray(leadData.notes) ? leadData.notes : [];
  // If the lead already has a "real" ad-form note (leadgen webhook /
  // Google Lead Form / TikTok lead), suppress the messenger fallback —
  // both arrived for Click-to-Messenger leads and we'd otherwise show
  // the same form twice. Only fall back when the ad note is missing.
  const hasAdFormNote = rawNotes.some((n) => AD_FORM_SOURCES.has(n?.source || ''));
  const allNotes = rawNotes.filter((n) => {
    const src = n?.source || 'web';
    if (LEAD_DETAIL_SOURCES.has(src)) return true;
    if (!hasAdFormNote && FORM_FALLBACK_SOURCES.has(src) && looksLikeFormSubmission(n?.content)) return true;
    return false;
  });
  const sortedNotes = [...allNotes].sort((a, b) => {
    const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bTime - aTime;
  });

  // Per-source counts for the filter chips. We collapse all ad form
  // sources under a single "form" bucket so the chip row stays short
  // even on accounts that mix Meta / Google / TikTok lead ads.
  // Messenger / Instagram notes only land in this list when their body
  // is form-shaped (Click-to-Messenger fallback above), so we count
  // them as form too — the operator filed them as form data.
  const isFormNote = (n) => {
    const src = n?.source || '';
    if (AD_FORM_SOURCES.has(src)) return true;
    // Messenger / Instagram fallback notes only enter `allNotes` when no
    // real ad-form note exists, so reaching here means it's a form
    // submission posing as a DM. Count it under the form chip.
    if (FORM_FALLBACK_SOURCES.has(src) && looksLikeFormSubmission(n?.content)) return true;
    return false;
  };
  const timelineCounts = sortedNotes.reduce(
    (acc, n) => {
      acc.all += 1;
      if (isFormNote(n)) {
        acc.form += 1;
      } else {
        // AI receptionist calls count under the "Calls" chip with the
        // human VoIP calls — one bucket for everything phone-shaped.
        const src = n?.source === 'phone_agent' ? 'phone' : (n?.source || 'web');
        acc[src] = (acc[src] || 0) + 1;
      }
      return acc;
    },
    { all: 0, web: 0, appointment: 0, form: 0, phone: 0 }
  );

  const filteredNotes = timelineFilter === 'all'
    ? sortedNotes
    : timelineFilter === 'form'
      ? sortedNotes.filter(isFormNote)
      : timelineFilter === 'phone'
        ? sortedNotes.filter((n) => n?.source === 'phone' || n?.source === 'phone_agent')
        : sortedNotes.filter((n) => (n?.source || 'web') === timelineFilter);

  const totalNotes = filteredNotes.length;
  const totalNotePages = Math.max(1, Math.ceil(totalNotes / notesPerPage));
  const currentNotesPage = Math.min(notesPage, totalNotePages);
  const noteStartIndex = totalNotes === 0 ? 0 : (currentNotesPage - 1) * notesPerPage;
  const noteEndIndex = Math.min(noteStartIndex + notesPerPage, totalNotes);
  const paginatedNotes = filteredNotes.slice(noteStartIndex, noteEndIndex);

  const generateAIEmailContent = async (subject) => {
    if (!subject.trim()) {
      toast.error('Please enter a subject first');
      return;
    }

    if (subject.trim().length < 3) {
      toast.error('Subject must be at least 3 characters long');
      return;
    }

    setIsGeneratingAI(true);
    toast.loading('Generating GPT-4 email content...');
    try {
      // Lead bilgilerini kullanarak AI prompt oluştur
      const prompt = `⚠️ CRITICAL: You are ONLY allowed to write the email content. DO NOT write greetings, closings, signatures, or contact information.

First, create a professional and engaging email subject line based on this input: "${subject}"

Lead Information:
- Name: ${leadData.firstName} ${leadData.lastName}
- Company: ${leadData.company || 'N/A'}
- Position: ${leadData.position || 'N/A'}
- Industry: ${leadData.industry || 'N/A'}
- Lead Status: ${leadData.status || 'N/A'}
- Lead Source: ${leadData.source || 'N/A'}

Company Information:
- Company Name: ${COMPANY_NAME}
- Sender Name: ${SENDER_NAME}
- Company Email: ${organization?.data?.organization?.email || 'noreply@yourcompany.com'}
- Company Website: ${COMPANY_WEBSITE}

Subject Line Requirements:
1. Make it professional and engaging
2. Include the company name if available
3. Make it specific to the topic mentioned
4. Keep it concise (under 60 characters)
5. Use proper capitalization

Then, write a professional email message that directly relates to and expands on the improved subject line.

Email Content Requirements:
1. The email content MUST directly address and expand on the improved subject line
2. Use the subject as the main topic/focus of the email
3. Make the content relevant to the specific subject mentioned
4. Keep it professional but friendly
5. Personalize using the lead's information
6. Make it concise (2-3 paragraphs maximum)
7. End with a clear call to action related to the subject
8. Use the lead's name and company context naturally in the content
9. Make the email feel personal and tailored to this specific lead
10. Use the company information provided above for professional context
11. DO NOT include any greeting (Dear, Hello, Hi, etc.)
12. DO NOT include any closing phrases like "Best regards", "Regards", "Sincerely" - just end with the call to action
13. DO NOT include the sender's name or contact information
14. DO NOT include email signature - the system will add it automatically
15. CRITICAL: Never write "Best regards", "Regards", "Sincerely" or any similar closing phrases
16. CRITICAL: Never include sender name, email, or website in the content
17. CRITICAL: End the email directly with the call to action, nothing else

Format your response exactly like this:
SUBJECT: [Your improved subject line here]
CONTENT: [Your email content here]

⚠️ FINAL WARNING: 
- DO NOT write "Best regards", "Regards", "Sincerely" or any closing
- DO NOT write sender name, email, or website
- DO NOT write any signature
- End directly with your call to action
- The system will add the signature automatically

Example: If input is "tomorrow event", create a subject like "Tomorrow's Event at ${COMPANY_NAME}" and then write the email content.`;

      // Server proxy üzerinden OpenAI API çağrısı yap
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Please log in to use AI features.');
      }

      const response = await fetch(`${API_BASE_URL}/openai/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          model: 'gpt-4',
          messages: [
            {
              role: 'system',
              content: `You are an expert professional email writer with deep understanding of business communication. Your task is to first create an improved, professional subject line based on the user input, then write email content that directly relates to and expands on that improved subject line. The content must be focused on the specific subject, not generic. Always use the subject as the main topic and make every sentence relevant to it. Use the lead's information to create highly personalized and contextual content. 

CRITICAL RULES - NEVER VIOLATE:
- Format your response exactly as "SUBJECT: [improved subject line]" followed by "CONTENT: [email content]"
- DO NOT include greetings (Dear, Hello, Hi, etc.)
- DO NOT include closing phrases (Best regards, Regards, Sincerely, etc.)
- DO NOT include sender name, email, or website
- DO NOT include any signature or contact information
- End the email directly with the call to action
- The system will automatically add the signature

Use the company information provided to create more professional and contextual emails.`
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 400,
          temperature: 0.6
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OpenAI API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      const aiResponse = data.choices[0].message.content;

      // AI yanıtını parse et
      let improvedSubject = subject; // Varsayılan olarak orijinal subject'i kullan
      let emailContent = aiResponse; // Varsayılan olarak tüm yanıtı kullan

      // SUBJECT: ve CONTENT: formatını kontrol et
      if (aiResponse.includes('SUBJECT:') && aiResponse.includes('CONTENT:')) {
        const subjectMatch = aiResponse.match(/SUBJECT:\s*(.+?)(?=\n|CONTENT:)/i);
        const contentMatch = aiResponse.match(/CONTENT:\s*(.+)/is);

        if (subjectMatch && contentMatch) {
          improvedSubject = subjectMatch[1].trim();
          emailContent = contentMatch[1].trim();
        }
      }

      // Template'de zaten imza var, hiçbir şekilde imza ekleme

      // Hem subject'i hem de message'ı güncelle
      setEmailData(prev => ({
        ...prev,
        subject: improvedSubject,
        message: emailContent
      }));

      toast.dismiss();
      toast.success('GPT-4 email content generated successfully!');
    } catch (error) {
      console.error('Content generation error:', error);
      toast.dismiss();
      toast.error('Failed to generate content');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // SMS AI Content Generation
  const generateAISMSContent = async (topic) => {
    if (!topic.trim()) {
      toast.error('Please enter a topic first');
      return;
    }

    setIsGeneratingSMSAI(true);
    toast.loading('Generating AI SMS content...');
    try {
      const prompt = `Write a professional SMS message about: "${topic}"

Recipient Information:
- Name: ${leadData.firstName} ${leadData.lastName}
- Company: ${leadData.company || 'N/A'}
- Position: ${leadData.position || 'N/A'}

Sender: ${SENDER_NAME}

SMS Requirements:
1. Keep it under 160 characters if possible
2. Be concise and direct
3. Use a professional but friendly tone
4. Include a clear call to action
5. Personalize with the recipient's name
6. DO NOT include any greeting like "Hi" or "Hello" - start directly with the message
7. DO NOT include sender name or signature at the end
8. Make every word count - SMS space is limited

Respond with ONLY the SMS message text, nothing else.`;

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Please log in to use AI features.');
      }

      const response = await fetch(`${API_BASE_URL}/openai/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          model: 'gpt-4',
          messages: [
            {
              role: 'system',
              content: 'You are an expert SMS copywriter. Write concise, professional SMS messages. Keep messages under 160 characters when possible. Respond with ONLY the SMS text, no explanations or formatting.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 100,
          temperature: 0.6
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OpenAI API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      const smsContent = data.choices[0].message.content.trim();

      setSmsData(prev => ({
        ...prev,
        message: smsContent
      }));

      toast.dismiss();
      toast.success('AI SMS content generated!');
    } catch (error) {
      console.error('SMS content generation error:', error);
      toast.dismiss();
      toast.error('Failed to generate SMS content');
    } finally {
      setIsGeneratingSMSAI(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        onConfirm={handleConfirmAction}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText="Delete"
        isDanger={true}
        isLoading={confirmationModal.actionType === 'delete_lead' ? deleteMutation.isLoading : deleteNoteMutation.isLoading}
      />
      <ConfirmationModal {...claim.confirmProps} />

      <SettingsRequiredModal
        isOpen={settingsRequiredModal.isOpen}
        onClose={() => setSettingsRequiredModal((prev) => ({ ...prev, isOpen: false }))}
        type={settingsRequiredModal.type}
        canManageSettings={canManageSettings}
      />

      {/* Attachment preview lightbox */}
      {attachmentPreview.isOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-2 sm:p-4"
          onClick={closeAttachmentPreview}
        >
          <div
            className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
              <p
                className="text-sm font-medium text-gray-900 truncate"
                title={attachmentPreview.att?.originalName}
              >
                {attachmentPreview.att?.originalName}
              </p>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => handleDownloadAttachment(attachmentPreview.att)}
                  className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                  title="Download"
                >
                  <Download size={18} />
                </button>
                <button
                  type="button"
                  onClick={closeAttachmentPreview}
                  className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                  title="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto flex items-center justify-center bg-gray-50 rounded-b-lg">
              {attachmentPreview.loading ? (
                <div className="my-24 animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
              ) : attachmentPreview.att?.mimeType === 'application/pdf' ? (
                <iframe
                  title="Attachment preview"
                  src={attachmentPreview.url}
                  className="w-full h-[75vh] rounded-b-lg"
                />
              ) : (
                <img
                  src={attachmentPreview.url}
                  alt={attachmentPreview.att?.originalName}
                  className="max-w-full max-h-[75vh] object-contain"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {showDncClear && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !clearDncMutation.isLoading && setShowDncClear(false)}>
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900">Remove from Do Not Call</h3>
            <p className="mt-1 text-sm text-gray-600">
              Only do this if {leadData.firstName || 'this person'} confirmed it is OK to contact them again. The reason is saved on the lead's timeline.
            </p>
            <textarea
              value={dncReason}
              onChange={(e) => setDncReason(e.target.value)}
              rows={3}
              maxLength={500}
              autoFocus
              placeholder="e.g. Called us back on 9/26 and asked for a consultation"
              className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button type="button" className="btn-secondary" onClick={() => setShowDncClear(false)} disabled={clearDncMutation.isLoading}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={dncReason.trim().length < 5 || clearDncMutation.isLoading}
                onClick={() => clearDncMutation.mutate(dncReason.trim())}
              >
                {clearDncMutation.isLoading ? 'Removing…' : 'Remove from Do Not Call'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div className="flex items-start gap-4 min-w-0">
          <LeadAvatar lead={leadData} size={56} className="hidden sm:flex mt-0.5" />
          <LeadAvatar lead={leadData} size={44} className="sm:hidden mt-0.5" />
          <div className="min-w-0">
          <h1 className={`text-3xl font-bold flex items-center gap-3 ${leadData.status === 'do_not_call' ? 'text-gray-500' : 'text-gray-900'}`}>
            {leadData.status === 'do_not_call' && <PhoneOff size={28} className="text-gray-500" />}
            <span className={leadData.status === 'do_not_call' ? 'line-through' : ''}>
              {formatName(leadData.firstName, leadData.lastName)}
            </span>
          </h1>
          <p className="mt-2 text-gray-600">
            {leadData.company && `${leadData.company} • `}{leadData.position}
          </p>
          <SocialLinks lead={leadData} />
          {leadData.status !== 'do_not_call' && <ReengagedNotice lead={leadData} className="mt-2 max-w-2xl" />}
          {leadData.smsOptOut?.optedOutAt && (
            <div className="mt-2 max-w-2xl flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <span>
                <span className="font-medium">Opted out of texts</span> — replied "{String(leadData.smsOptOut.keyword || 'stop').toUpperCase()}" on {new Date(leadData.smsOptOut.optedOutAt).toLocaleDateString()}. SMS is blocked until they text START. Calls and email still work.
              </span>
            </div>
          )}
          {leadData.status === 'do_not_call' && (
            <div className="mt-2 flex flex-col gap-2 max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-800 text-white">
                  <PhoneOff size={14} className="mr-2" />
                  Do Not Call - This lead has requested not to be contacted by phone
                </span>
                <button
                  type="button"
                  onClick={() => setShowDncClear(true)}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline"
                >
                  Remove from Do Not Call
                </button>
              </div>
              {leadData.dnc?.reengagedAt && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
                  <span>
                    Reached out again via {DNC_CHANNEL_LABELS[leadData.dnc.reengagedChannel] || leadData.dnc.reengagedChannel}
                    {' '}on {new Date(leadData.dnc.reengagedAt).toLocaleString()}. Calls and automatic messages stay blocked.
                    {['sms', 'whatsapp'].includes(leadData.dnc.reengagedChannel) && ` You can send one ${DNC_CHANNEL_LABELS[leadData.dnc.reengagedChannel]} reply to ask whether it is OK to contact them.`}
                    {' '}Once they confirm, remove them from Do Not Call.
                  </span>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-wrap gap-3">
          <button
            onClick={() => navigate(-1)}
            className="btn-secondary"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back
          </button>
          <Link
            to={`/inbox?leadId=${id}`}
            className="btn-secondary"
            title="Open this lead's conversations in the Inbox"
          >
            <Inbox size={16} className="mr-2" />
            Inbox
          </Link>
          {/* FCFS reservation — only visible while the lead is open. */}
          {claim.unassigned && (
            <button
              onClick={() => claim.requestClaim()}
              className="btn-primary bg-amber-600 hover:bg-amber-700"
              title="Reserve this lead for yourself before working on it"
            >
              <UserCheck size={16} className="mr-2" />
              Take this lead
            </button>
          )}
          {leadData.email && (
            <button
              onClick={handleEmailButtonClick}
              className={`${leadData.emailPreferences && !leadData.emailPreferences.subscribed
                ? 'bg-orange-600 hover:bg-orange-700 text-white btn-primary'
                : 'btn-primary'}`}
              title={leadData.emailPreferences && !leadData.emailPreferences.subscribed ? 'This person is unsubscribed from the newsletter' : ''}
            >
              <Send size={16} className="mr-2" />
              Send Email
              {leadData.emailPreferences && !leadData.emailPreferences.subscribed && (
                <span className="ml-1 text-xs bg-red-100 text-red-800 px-1.5 py-0.5 rounded-full">
                  ⚠️
                </span>
              )}
            </button>
          )}
          {leadNumbers(leadData).length > 0 && (
            <button
              onClick={handleSMSButtonClick}
              className="btn-primary bg-teal-600 hover:bg-teal-700"
            >
              <MessageSquare size={16} className="mr-2" />
              Send SMS
            </button>
          )}
          {currentUser?.permissions?.edit_lead && (
            <Link
              to={`/leads/${id}/edit`}
              className="btn-primary"
            >
              <Edit size={16} className="mr-2" />
              Edit
            </Link>
          )}
          {currentUser?.permissions?.delete_lead && (
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isLoading}
              className="btn-danger"
            >
              <Trash2 size={16} className="mr-2" />
              Delete
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <User size={20} className="mr-2" />
                Personal Information
              </h3>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {leadData.firstName && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">First Name</label>
                    <p className="mt-1 text-sm text-gray-900">{leadData.firstName}</p>
                  </div>
                )}
                {leadData.lastName && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Name</label>
                    <p className="mt-1 text-sm text-gray-900">{leadData.lastName}</p>
                  </div>
                )}
                {leadData.email && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900">
                      <a
                        href={`mailto:${leadData.email}`}
                        className={`text-primary-600 hover:text-primary-700 ${claim.locked ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={(e) => {
                          // FCFS — launching the external mail client is
                          // intent to reach out; route through the claim
                          // guard so it isn't a silent escape from the
                          // ownership model.
                          if (claim.ownerSelf) return;
                          e.preventDefault();
                          claim.requestClaim(() => { window.location.href = `mailto:${leadData.email}`; });
                        }}
                      >
                        {formatEmail(leadData.email)}
                      </a>
                    </p>
                  </div>
                )}
                <LeadPhoneNumbers lead={leadData} claim={claim} />
                {leadData.position && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Position</label>
                    <p className="mt-1 text-sm text-gray-900">{leadData.position}</p>
                  </div>
                )}
              </div>
            </div>
          </div>



          {/* Company Information */}
          {(currentUser?.permissions?.view_company_info) && (leadData.company || leadData.position || leadData.website || leadData.companyEmail || leadData.companyPhone) && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Building size={20} className="mr-2" />
                  Company Information
                </h3>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {leadData.company && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Company</label>
                      <p className="mt-1 text-sm text-gray-900">{formatCompany(leadData.company)}</p>
                    </div>
                  )}
                  {leadData.position && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Position</label>
                      <p className="mt-1 text-sm text-gray-900">{leadData.position}</p>
                    </div>
                  )}
                  {leadData.companyEmail && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Company Email</label>
                      <p className="mt-1 text-sm text-gray-900">
                        <a
                          href={`mailto:${leadData.companyEmail}`}
                          className={`text-primary-600 hover:text-primary-700 ${claim.locked ? 'opacity-50 cursor-not-allowed' : ''}`}
                          onClick={(e) => {
                            if (claim.ownerSelf) return;
                            e.preventDefault();
                            claim.requestClaim(() => { window.location.href = `mailto:${leadData.companyEmail}`; });
                          }}
                        >
                          {leadData.companyEmail}
                        </a>
                      </p>
                    </div>
                  )}
                  {leadData.website && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Website</label>
                      <p className="mt-1 text-sm text-gray-900">
                        <a
                          href={formatWebsite(leadData.website)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700"
                        >
                          {leadData.website}
                        </a>
                      </p>
                    </div>
                  )}
                  {leadData.companyPhone && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Company Phone</label>
                      <p className="mt-1 text-sm text-gray-900">
                        <span className="inline-flex items-center gap-2">
                          <a
                            href={`tel:${leadData.companyPhone}`}
                            className={`text-primary-600 hover:text-primary-700 ${claim.locked ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onClick={(e) => {
                              if (claim.ownerSelf) return;
                              e.preventDefault();
                              claim.requestClaim(() => { window.location.href = `tel:${leadData.companyPhone}`; });
                            }}
                          >
                            {formatPhone(leadData.companyPhone)}
                          </a>
                          <BrowserCallButton
                            lead={leadData}
                            phone={leadData.companyPhone}
                            disabled={claim.locked}
                            guard={(go) => (claim.ownerSelf ? go() : claim.requestClaim(go))}
                          />
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Address */}
          {leadData.address && (leadData.address.street || leadData.address.city || leadData.address.state || leadData.address.country) && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <MapPin size={20} className="mr-2" />
                  Address Information
                </h3>
              </div>
              <div className="card-body">
                <p className="text-sm text-gray-900">{formatAddress(leadData.address)}</p>
              </div>
            </div>
          )}

          {/* Live AI-receptionist call for THIS lead — streams the
              conversation in real time while the call is in progress;
              renders nothing otherwise. */}
          <PhoneAgentLiveCall leadId={id} />

          {/* Latest SMS / email / DM exchange; full threads stay in the Inbox. */}
          <LeadRecentMessages leadId={id} />

          {/* Notes — operator notebook for this lead. Customer-facing
              communications live in the Inbox; this is for internal
              memos, appointment markers, and anything the team should
              remember about the lead. */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center space-x-2">
                <FileText size={20} className="mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Notes</h3>
              </div>
              <div className="flex items-center space-x-2">
                {hasAutoRepairPermission && (
                  <button
                    onClick={() => setShowCreateServiceRecordModal(true)}
                    className="text-sm bg-blue-50 text-blue-700 px-3 py-1 rounded-md hover:bg-blue-100 transition-colors flex items-center border border-blue-200"
                  >
                    <FileIcon size={16} className="mr-1" />
                    Service Record
                  </button>
                )}
                <button
                  onClick={() => setShowNoteForm(!showNoteForm)}
                  className="text-sm bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 transition-colors"
                >
                  {showNoteForm ? 'Cancel' : 'Add Note'}
                </button>
              </div>
            </div>
            <div className="card-body">
              {/* Filter chips — show when more than one bucket has
                  entries; a single-bucket lead doesn't need filters. */}
              {allNotes.length > 0 && (timelineCounts.appointment > 0 || timelineCounts.form > 0 || timelineCounts.phone > 0) && (
                <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-gray-100">
                  {[
                    { key: 'all',         label: 'All',           color: 'bg-gray-100 text-gray-700',       active: 'bg-gray-800 text-white' },
                    { key: 'web',         label: 'Notes',         color: 'bg-blue-50 text-blue-700',        active: 'bg-blue-600 text-white' },
                    { key: 'form',        label: 'Form submissions', color: 'bg-indigo-50 text-indigo-700', active: 'bg-indigo-600 text-white' },
                    { key: 'phone',       label: 'Calls',         color: 'bg-green-50 text-green-700',      active: 'bg-green-600 text-white' },
                    { key: 'appointment', label: 'Appointments',  color: 'bg-orange-50 text-orange-700',    active: 'bg-orange-600 text-white' }
                  ].map((chip) => {
                    const count = timelineCounts[chip.key] || 0;
                    if (chip.key !== 'all' && count === 0) return null;
                    const isActive = timelineFilter === chip.key;
                    return (
                      <button
                        key={chip.key}
                        onClick={() => { setTimelineFilter(chip.key); setNotesPage(1); }}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${isActive ? chip.active : chip.color + ' hover:opacity-80'}`}
                      >
                        {chip.label}
                        <span className={`ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 rounded-full text-[10px] ${isActive ? 'bg-white/20' : 'bg-white/60'}`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Add Note Form */}
              {showNoteForm && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (newNote.trim()) {
                          handleAddNote();
                        }
                      }
                    }}
                    placeholder="Enter your note here... (Press Enter to save, Shift+Enter for new line)"
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    rows="3"
                    maxLength="10000"
                  />
                  {noteCustomFieldDefs.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <CustomFieldsInput entity="note" values={noteCustomFields} onChange={setNoteCustomFields} />
                    </div>
                  )}
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-500">
                      {newNote.length}/10000 characters
                    </span>
                    <button
                      onClick={handleAddNote}
                      disabled={!newNote.trim() || addNoteMutation.isLoading}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {addNoteMutation.isLoading ? 'Adding...' : 'Add Note'}
                    </button>
                  </div>
                </div>
              )}

              {/* Notes List */}
              {allNotes.length > 0 ? (
                <div className="space-y-4">
                  {/* Notes pagination controls */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      {totalNotes > 0 ? `Showing ${noteStartIndex + 1}-${noteEndIndex} of ${totalNotes}` : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Per page</span>
                      <select
                        value={notesPerPage}
                        onChange={(e) => {
                          const next = Number(e.target.value);
                          setNotesPerPage(Number.isFinite(next) ? next : 5);
                          setNotesPage(1);
                        }}
                        className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                  </div>

                  {totalNotes === 0 && (
                    <p className="text-sm text-gray-500 italic text-center py-6">
                      No {timelineFilter !== 'all' ? timelineFilter : ''} entries in this timeline.
                    </p>
                  )}
                  {paginatedNotes.map((note, index) => (
                    <div key={note._id || index} className={`border rounded-lg p-4 ${note.source === 'phone' ? 'border-green-200 bg-green-50' :
                      note.source === 'phone_agent' ? 'border-cyan-200 bg-cyan-50' :
                      note.source === 'sms' ? 'border-teal-200 bg-teal-50' :
                        note.source === 'email' ? 'border-purple-200 bg-purple-50' :
                          note.source === 'web' ? 'border-blue-200 bg-blue-50' :
                            note.source === 'appointment' ? 'border-orange-200 bg-orange-50' :
                              'border-gray-200'
                      }`}>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center space-x-2">
                          <div className="text-xs text-gray-500">
                            {(() => {
                              // createdBy is populated as {name,email} on
                              // the read path; legacy embedded notes may
                              // still carry just an ObjectId. Surface
                              // whatever signal we have so the operator
                              // never sees an anonymous note.
                              const cb = note.createdBy;
                              let author = null;
                              if (cb?.name) author = cb.name;
                              else if (cb?.email) author = cb.email;
                              else if (cb) author = 'Unknown user';
                              else author = 'System';
                              return (
                                <>
                                  <span className="font-medium text-gray-700">{author}</span>
                                  <span className="mx-1">·</span>
                                </>
                              );
                            })()}
                            {formatDateTime(note.createdAt)}
                          </div>
                          {note.source && (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getNoteSourceColor(note.source)}`}>
                              {getNoteSourceIcon(note.source)} {getNoteSourceText(note.source)}
                            </span>
                          )}
                          {note.direction === 'inbound' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700 border border-blue-200">
                              ← Inbound
                            </span>
                          )}
                          {note.direction === 'outbound' && (note.source !== 'web') && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200">
                              Outbound →
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          {hasAutoRepairPermission && (
                            <button
                              onClick={() => handlePrintSingleNote(note)}
                              className="text-gray-400 hover:text-blue-600 transition-colors"
                              title="Print this note"
                            >
                              <Printer size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteNote(note._id)}
                            className="text-red-400 hover:text-red-700 transition-colors"
                            title="Delete note"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                      {note.content && note.content.includes('autolab-service-record-container') ? (
                        <div 
                          onClick={() => {
                            setSelectedServiceRecord(note.content);
                            setShowServiceRecordModal(true);
                          }}
                          className="mt-2 flex items-center p-3 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors group shadow-sm"
                        >
                          <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                            <FileIcon className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="ml-4 flex-1">
                            <h4 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                              Service Record
                            </h4>
                            <p className="text-xs text-gray-500 mt-1">
                              Automated service history details. Click to view full record.
                            </p>
                          </div>
                          <div className="ml-4">
                            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full group-hover:bg-blue-100">
                              View Record
                            </span>
                          </div>
                        </div>
                      ) : FORM_NOTE_SOURCES.has(note.source) || looksLikeFormSubmission(note.content) ? (
                        <MetaLeadFormCard
                          content={note.content}
                          leadgenId={leadgenIdFromTag}
                          onPhoneClick={(phone, e) => {
                            if (claim.ownerSelf) return;
                            e.preventDefault();
                            claim.requestClaim(() => { window.location.href = `tel:${phone}`; });
                          }}
                          onEmailClick={(email, e) => {
                            if (claim.ownerSelf) return;
                            e.preventDefault();
                            claim.requestClaim(() => { window.location.href = `mailto:${email}`; });
                          }}
                        />
                      ) : (
                        <div
                          className="text-sm text-gray-900 whitespace-pre-wrap note-content"
                          dangerouslySetInnerHTML={{ __html: linkifyText(note.content) }}
                          style={{
                            wordBreak: 'break-word',
                            lineHeight: '1.5'
                          }}
                        />
                      )}
                      {/* VoIP call recording — render a native audio
                          player when the CDR poller stamped a recording
                          filename onto this note's customFields. The
                          actual audio bytes are proxied through the CRM
                          server so the NS token never reaches the
                          browser (see /api/voip/recording/note/:id). */}
                      {note.source === 'phone' && (() => {
                        const cf = note.customFields instanceof Map
                          ? Object.fromEntries(note.customFields)
                          : (note.customFields || {});
                        // has_recording is stamped by the CDR poller after
                        // probing NS — only true when a real audio file
                        // exists. Avoids rendering a broken player for
                        // never-recorded calls.
                        if (!cf.has_recording) return null;
                        return (
                          <div className="mt-3 pt-3 border-t border-green-200/60">
                            <VoipRecordingPlayer kind="note" id={note._id} />
                            <CallTranscriptPanel noteId={note._id} initialCf={cf} />
                          </div>
                        );
                      })()}
                      {/* Phone Agent call recording — same inline player,
                          streamed through the phone-agent proxy (the AI
                          platform's storage URLs are short-lived and
                          access-controlled, so playback goes via our
                          server). Transcript lives in the note body. */}
                      {note.source === 'phone_agent' && (() => {
                        const cf = note.customFields instanceof Map
                          ? Object.fromEntries(note.customFields)
                          : (note.customFields || {});
                        // Only end-of-call notes carry a recording/transcript.
                        // Mid-call tool notes (capture_lead etc.) have nothing
                        // to play or summarize.
                        if (!cf.end_of_call && !cf.has_recording && !cf.transcript) return null;
                        return (
                          <div className="mt-3 pt-3 border-t border-cyan-200/60">
                            {cf.has_recording && (
                              <VoipRecordingPlayer srcPath={`phone-agent/recording/note/${note._id}`} />
                            )}
                            <CallTranscriptPanel noteId={note._id} initialCf={cf} basePath="phone-agent" inlineTranscript />
                          </div>
                        );
                      })()}
                      {note.customFields && note.source !== 'phone' && (
                        <CustomFieldsDisplay entity="note" values={note.customFields} wrapperClassName="mt-3 pt-3 border-t border-gray-200/60" />
                      )}
                    </div>
                  ))}

                  {/* Pagination buttons */}
                  {totalNotePages > 1 && (
                    <div className="flex items-center justify-between pt-2">
                      <button
                        type="button"
                        onClick={() => setNotesPage((p) => Math.max(1, p - 1))}
                        disabled={currentNotesPage <= 1}
                        className="text-sm px-3 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <div className="text-sm text-gray-600">
                        Page {currentNotesPage} / {totalNotePages}
                      </div>
                      <button
                        type="button"
                        onClick={() => setNotesPage((p) => Math.min(totalNotePages, p + 1))}
                        disabled={currentNotesPage >= totalNotePages}
                        className="text-sm px-3 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No notes yet. Add your first note above.</p>
              )}
            </div>
          </div>
          {/* Attachments */}
          <div className="card">
            <div className="card-header">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Paperclip size={20} className="mr-2" />
                  Attachments
                  {(leadData.attachments?.length || 0) > 0 && (
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      ({leadData.attachments.length})
                    </span>
                  )}
                </h3>
                {currentUser?.permissions?.edit_lead && (
                  <label
                    className={`btn-secondary cursor-pointer ${uploadAttachmentsMutation.isLoading ? 'opacity-60 pointer-events-none' : ''}`}
                  >
                    <Upload size={16} className="mr-2" />
                    {uploadAttachmentsMutation.isLoading ? 'Uploading…' : 'Add files'}
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleAttachmentSelect}
                      disabled={uploadAttachmentsMutation.isLoading}
                    />
                  </label>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Up to 10 files at once, 10 MB each. Photos are compressed automatically without visible quality loss.
              </p>
            </div>
            <div className="card-body">
              {(leadData.attachments?.length || 0) === 0 ? (
                <p className="text-sm text-gray-500 italic">No attachments yet.</p>
              ) : (
                <div className="space-y-2">
                  {leadData.attachments.map((att) => {
                    const isImage = (att.mimeType || '').startsWith('image/');
                    return (
                      <div
                        key={att._id}
                        className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <button
                            type="button"
                            onClick={() => openAttachmentPreview(att)}
                            className="w-9 h-9 rounded bg-blue-100 flex items-center justify-center flex-shrink-0 overflow-hidden"
                            title={isPreviewableAttachment(att) ? 'Preview' : 'Download'}
                          >
                            {isImage ? (
                              <AttachmentThumb
                                leadId={id}
                                attachment={att}
                                className="w-9 h-9 object-cover"
                              />
                            ) : (
                              <FileIcon size={16} className="text-blue-600" />
                            )}
                          </button>
                          <div className="min-w-0">
                            <button
                              type="button"
                              onClick={() => openAttachmentPreview(att)}
                              className="block max-w-full text-left"
                            >
                              <p
                                className="text-sm font-medium text-gray-900 truncate hover:text-blue-600"
                                title={att.originalName}
                              >
                                {att.originalName}
                              </p>
                            </button>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(att.size)}
                              {att.originalSize && att.size < att.originalSize && (
                                <span className="text-green-600">
                                  {' '}(compressed from {formatFileSize(att.originalSize)})
                                </span>
                              )}
                              {att.uploadedAt && (
                                <> • {new Date(att.uploadedAt).toLocaleDateString()}</>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {isPreviewableAttachment(att) && (
                            <button
                              type="button"
                              onClick={() => openAttachmentPreview(att)}
                              className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                              title="Preview"
                            >
                              <Eye size={16} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleDownloadAttachment(att)}
                            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                            title="Download"
                          >
                            <Download size={16} />
                          </button>
                          {currentUser?.permissions?.edit_lead && (
                            <button
                              type="button"
                              onClick={() => handleDeleteAttachment(att)}
                              disabled={deleteAttachmentMutation.isLoading}
                              className="p-2 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-50"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Lead Details */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Tag size={20} className="mr-2" />
                Lead Details
              </h3>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Status
                  <span className="ml-1.5 text-[10px] font-normal text-gray-400 uppercase tracking-wide">(stage)</span>
                </label>
                {(() => {
                  const currentStage = stages.find(
                    (s) => String(s._id) === String(leadData.stageId)
                  );
                  const bg = currentStage?.color ? `${currentStage.color}22` : '#F3F4F6';
                  const fg = currentStage?.color || '#374151';
                  return (
                    <select
                      value={leadData.stageId || ''}
                      onChange={(e) => handleStageChange(e.target.value)}
                      disabled={updateLeadMutation.isLoading || stages.length === 0}
                      style={{ backgroundColor: bg, color: fg }}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 border-0 cursor-pointer focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                    >
                      {stages.map((s) => (
                        <option key={s._id} value={s._id}>{s.name}</option>
                      ))}
                    </select>
                  );
                })()}
                {leadData?.opendental?.syncStatus && leadData.opendental.syncStatus !== 'none' && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <OpenDentalSyncBadge status={leadData.opendental.syncStatus} />
                    {/* Replay only works when the lead carries a stored
                        booking intent (branch chosen at save time). Without
                        it the retry job can only answer "no branch on lead —
                        needs manual booking", so those leads get the manual
                        Schedule link below instead. */}
                    {(leadData.opendental.syncStatus === 'pending' || leadData.opendental.syncStatus === 'failed') &&
                      leadData.opendental.locationId && (
                      <button
                        type="button"
                        onClick={() => retrySyncMutation.mutate()}
                        disabled={retrySyncMutation.isLoading}
                        className="text-xs font-medium text-teal-700 hover:text-teal-800 underline disabled:opacity-50"
                      >
                        {retrySyncMutation.isLoading ? 'Syncing…' : 'Sync now'}
                      </button>
                    )}
                    {leadData.opendental.syncStatus === 'failed' && leadData.opendental.syncError && (
                      <span className="text-xs text-red-600" title={leadData.opendental.syncError}>
                        {leadData.opendental.syncError.slice(0, 60)}
                      </span>
                    )}
                  </div>
                )}
                {/* Manual booking entry — the safety net for "Skip for now"
                    on the Won prompt AND for dead-end pending/failed leads
                    that carry no replayable intent. Gated on the CURRENT
                    stage's closedType (same signal the booking intercept
                    uses) — NOT lead.status: the legacy status field sticks
                    at 'won' when the lead moves back to an open stage whose
                    legacyStatus is null, which kept this button visible on
                    reopened leads. Exactly one action shows at a time:
                    replayable → Sync now, otherwise → this. */}
                {isOpenDentalReady &&
                  stages.some((s) => String(s._id) === String(leadData.stageId) && s.closedType === 'won') &&
                  leadData?.opendental?.syncStatus !== 'synced' &&
                  !leadData?.opendental?.locationId && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => openBookingForLead(leadData)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-md hover:bg-teal-100"
                    >
                      <Calendar size={13} />
                      Schedule OpenDental appointment
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Priority</label>
                <select
                  value={leadData.priority}
                  onChange={(e) => handlePriorityChange(e.target.value)}
                  disabled={updateLeadMutation.isLoading}
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 border-0 cursor-pointer focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors ${getPriorityColor(leadData.priority)}`}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Source</label>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 bg-gray-100 text-gray-800">
                  {getSourceText(leadData.source)}
                </span>
                {/* Which ad this lead came from (Meta / Google / TikTok). */}
                <AdAttributionBadge lead={leadData} className="mt-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Assigned To</label>
                <select
                  value={leadData.assignedTo || ''}
                  onChange={(e) => handleAssignChange(e.target.value)}
                  disabled={updateLeadMutation.isLoading || !currentUser?.permissions?.assign_lead}
                  title={!currentUser?.permissions?.assign_lead ? "You don't have permission to reassign leads" : undefined}
                  className={`mt-1 block w-full text-sm rounded-md border border-gray-300 bg-white px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${currentUser?.permissions?.assign_lead ? 'cursor-pointer' : 'cursor-not-allowed bg-gray-50'}`}
                >
                  <option value="">— Unassigned —</option>
                  {orgUsers.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.name || u.email}
                    </option>
                  ))}
                  {leadData.assignedTo &&
                    !orgUsers.some((u) => u._id === leadData.assignedTo) &&
                    leadData.assignedUser && (
                      <option value={leadData.assignedTo}>
                        {leadData.assignedUser.name || leadData.assignedUser.email}
                      </option>
                    )}
                </select>
              </div>
              {leadData.lastContactDate && leadData.assignedUser && (
                <div className="-mt-2">
                  <p className="text-xs text-gray-500">
                    Last reassigned {new Date(leadData.updatedAt || Date.now()).toLocaleDateString()}
                  </p>
                </div>
              )}
              {leadData.estimatedValue > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Estimated Value</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatCurrency(leadData.estimatedValue, leadData.currency)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Email Preferences */}
          {leadData.emailPreferences && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Mail size={20} className="mr-2" />
                  Email Preferences
                </h3>
              </div>
              <div className="card-body">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Email Subscription:</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${leadData.emailPreferences.subscribed
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                      }`}>
                      {leadData.emailPreferences.subscribed ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {!leadData.emailPreferences.subscribed && leadData.emailPreferences.unsubscribeDate && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-start">
                        <span className="text-red-400 text-sm mr-2">⚠️</span>
                        <div className="text-sm text-red-700">
                          <p><strong>Unsubscribe Date:</strong> {new Date(leadData.emailPreferences.unsubscribeDate).toLocaleDateString('en-US')}</p>
                          <p className="text-xs mt-1">Sending emails to this person may violate legal regulations.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tags */}
          {leadData.tags && leadData.tags.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Tag size={20} className="mr-2" />
                  Tags
                </h3>
              </div>
              <div className="card-body">
                <div className="flex flex-wrap gap-2">
                  {leadData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}



          {/* Business Card */}

          {leadData.businessCardImage && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
                  </svg>
                  Business Card
                </h3>
              </div>
              <div className="card-body">
                <div className="flex items-center justify-center">
                  <div className="relative">
                    <img
                      src={`${API_BASE_URL}/uploads/${leadData.businessCardImage}`}
                      alt="Business Card"
                      className="max-w-full h-auto rounded-lg shadow-md border border-gray-200"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-200 rounded-lg flex items-center justify-center">
                      <button
                        onClick={() => setShowBusinessCardModal(true)}
                        className="opacity-0 hover:opacity-100 transition-all duration-200 bg-white bg-opacity-95 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 shadow-lg hover:bg-opacity-100"
                      >
                        <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View Full Size
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-600">
                    Business card uploaded on {formatDateTime(leadData.createdAt)}
                  </p>
                  {leadData.source === 'business_card' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Lead created from business card analysis
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tasks */}
          <LeadTasksWidget leadId={leadData._id} />
          <LeadFollowUpWidget leadId={leadData._id} stageId={leadData.stageId} />
          <LeadActivityTimeline leadId={leadData._id} />

          {/* Custom Fields (hidden when nothing saved) */}
          {leadData.customFields && Object.keys(leadData.customFields instanceof Map ? Object.fromEntries(leadData.customFields) : leadData.customFields).length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Tag size={20} className="mr-2" />
                  Custom Information
                </h3>
              </div>
              <div className="card-body">
                <CustomFieldsDisplay entity="lead" values={leadData.customFields} />
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Calendar size={20} className="mr-2" />
                Timestamp Information
              </h3>
            </div>
            <div className="card-body space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Created At</label>
                <p className="mt-1 text-sm text-gray-900">{formatDateTime(leadData.createdAt)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                <p className="mt-1 text-sm text-gray-900">{formatDateTime(leadData.updatedAt)}</p>
              </div>
              {leadData.lastContactDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Contact</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(leadData.lastContactDate)}</p>
                </div>
              )}
              {leadData.nextFollowUpDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Next Follow Up</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(leadData.nextFollowUpDate)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div >

      {/* Email Modal */}
      {
        showEmailModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-5 mx-auto p-8 border w-full max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <Mail size={20} className="mr-2" />
                    Send Email
                  </h3>
                  <button
                    onClick={() => setShowEmailModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Unsubscribe Warning */}
                  {leadData.emailPreferences && !leadData.emailPreferences.subscribed && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <span className="text-red-400 text-lg">⚠️</span>
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-red-800">
                            Legal Warning
                          </h4>
                          <div className="mt-1 text-sm text-red-700">
                            <p>This person is unsubscribed from the newsletter.</p>
                            {leadData.emailPreferences.unsubscribeDate && (
                              <p className="mt-1">
                                <strong>Unsubscribe Date:</strong> {new Date(leadData.emailPreferences.unsubscribeDate).toLocaleDateString('en-US')}
                              </p>
                            )}
                            <p className="mt-2 text-xs">
                              Sending emails to this person may violate legal regulations.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Recipient
                    </label>
                    {leadData.email && leadData.companyEmail ? (
                      <select
                        value={selectedEmail || leadData.email}
                        onChange={(e) => setSelectedEmail(e.target.value)}
                        className="input w-full"
                      >
                        <option value={leadData.email}>{leadData.email} (Personal)</option>
                        <option value={leadData.companyEmail}>{leadData.companyEmail} (Company)</option>
                      </select>
                    ) : (
                      <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                        {leadData.email || leadData.companyEmail || 'No email address'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject *
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={emailData.subject}
                        onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                        className="input flex-1"
                        placeholder="Email subject"
                      />
                      <button
                        onClick={() => generateAIEmailContent(emailData.subject)}
                        disabled={isGeneratingAI || !emailData.subject.trim()}
                        className="relative px-4 py-2 bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:via-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 hover:shadow-lg font-medium text-sm border-0 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                        title="Generate GPT-4 email content based on subject and lead info"
                      >
                        {isGeneratingAI ? (
                          <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Generating...</span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <div className="w-5 h-5 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                              <span className="text-xs">✨</span>
                            </div>
                            <span>AI Generate</span>
                          </div>
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message *
                    </label>
                    <textarea
                      value={emailData.message}
                      onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                      className="input"
                      rows={10}
                      placeholder="Write your email message..."
                    />
                  </div>

                  {/* Fotoğraf Ekleme */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📎 Attachments
                    </label>
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors"
                      onDrop={(e) => {
                        e.preventDefault();
                        const files = Array.from(e.dataTransfer.files);
                        setEmailData(prev => ({
                          ...prev,
                          attachments: [...prev.attachments, ...files]
                        }));
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDragEnter={(e) => e.preventDefault()}
                    >
                      <input
                        type="file"
                        multiple
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={(e) => {
                          const files = Array.from(e.target.files);
                          setEmailData(prev => ({
                            ...prev,
                            attachments: [...prev.attachments, ...files]
                          }));
                        }}
                        className="hidden"
                        id="file-upload"
                      />
                      <label
                        htmlFor="file-upload"
                        className="cursor-pointer flex flex-col items-center space-y-2"
                      >
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                        </div>
                        <div>
                          <span className="text-blue-600 hover:text-blue-500 font-medium">Click to upload</span>
                          <span className="text-gray-500"> or drag and drop</span>
                        </div>
                        <p className="text-xs text-gray-500">PNG, JPG, PDF, DOC up to 10MB</p>
                      </label>
                    </div>

                    {/* Yüklenen Dosyalar */}
                    {emailData.attachments.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">Attached Files:</h4>
                        {emailData.attachments.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                                {file.type.startsWith('image/') ? (
                                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                ) : (
                                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                )}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                setEmailData(prev => ({
                                  ...prev,
                                  attachments: prev.attachments.filter((_, i) => i !== index)
                                }));
                              }}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      onClick={() => setShowEmailModal(false)}
                      className="btn-secondary"
                      disabled={sendEmailMutation.isLoading}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendEmail}
                      disabled={sendEmailMutation.isLoading}
                      className={`${leadData.emailPreferences && !leadData.emailPreferences.subscribed
                        ? 'bg-red-600 hover:bg-red-700 text-white btn-primary disabled:opacity-50 disabled:cursor-not-allowed'
                        : 'btn-primary'}`}
                    >
                      <Send size={16} className="mr-2" />
                      {sendEmailMutation.isLoading ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* SMS Modal */}
      {showSMSModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-8 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <MessageSquare size={20} className="mr-2" />
                  Send SMS
                </h3>
                <button
                  onClick={() => setShowSMSModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Phone number selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient
                  </label>
                  {leadNumbers(leadData).length > 1 ? (
                    <select
                      value={smsData.toPhone || leadData.phone || ''}
                      onChange={(e) => setSmsData({ ...smsData, toPhone: e.target.value })}
                      className="input w-full"
                    >
                      {leadNumbers(leadData).map((p) => (
                        <option key={p.last10 || p.number} value={p.number}>
                          {formatPhone(p.number)} ({p.label === 'primary' ? 'Main' : p.label.charAt(0).toUpperCase() + p.label.slice(1)})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {formatPhone(leadData.phone || leadNumbers(leadData)[0]?.number || '')}
                    </p>
                  )}
                </div>

                {/* Topic + AI Generate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Topic
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={smsData.topic}
                      onChange={(e) => setSmsData({ ...smsData, topic: e.target.value })}
                      className="input flex-1"
                      placeholder="Enter topic for AI generation..."
                    />
                    <button
                      onClick={() => generateAISMSContent(smsData.topic)}
                      disabled={isGeneratingSMSAI || !smsData.topic.trim()}
                      className="relative px-4 py-2 bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:via-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 hover:shadow-lg font-medium text-sm border-0 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                      title="Generate AI SMS content based on topic and lead info"
                    >
                      {isGeneratingSMSAI ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Generating...</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <div className="w-5 h-5 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                            <span className="text-xs">&#10024;</span>
                          </div>
                          <span>AI Generate</span>
                        </div>
                      )}
                    </button>
                  </div>
                </div>

                {leadData?.smsOptOut?.optedOutAt && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    This contact replied STOP and opted out of texts. SMS can't be sent until they text START.
                  </div>
                )}

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message *
                  </label>
                  <textarea
                    value={smsData.message}
                    onChange={(e) => setSmsData({ ...smsData, message: e.target.value })}
                    className="input"
                    rows={5}
                    placeholder="Write your SMS message..."
                    maxLength={918}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {smsData.message.length}/160 characters
                    {smsPartCount(smsData.message) > 1 && (
                      <span className="text-orange-500">
                        {' '}(goes out as {smsPartCount(smsData.message)} numbered texts)
                      </span>
                    )}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowSMSModal(false)}
                    className="btn-secondary"
                    disabled={sendSMSMutation.isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendSMS}
                    disabled={sendSMSMutation.isLoading || !!leadData?.smsOptOut?.optedOutAt}
                    className="btn-primary bg-teal-600 hover:bg-teal-700"
                  >
                    <Send size={16} className="mr-2" />
                    {sendSMSMutation.isLoading ? 'Sending...' : 'Send SMS'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Business Card Modal */}
      {
        showBusinessCardModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
                    </svg>
                    Business Card - {leadData?.firstName} {leadData?.lastName}
                  </h3>
                  <button
                    onClick={() => setShowBusinessCardModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="text-center">
                  <img
                    src={`${API_BASE_URL}/uploads/${leadData?.businessCardImage}`}
                    alt="Business Card"
                    className="max-w-full h-auto rounded-lg shadow-lg border border-gray-200 mx-auto"
                  />

                  <div className="mt-4 text-sm text-gray-600">
                    <p>Business card uploaded on {leadData && formatDateTime(leadData.createdAt)}</p>
                    {leadData?.source === 'business_card' && (
                      <p className="text-xs text-gray-500 mt-1">
                        Lead created from business card analysis
                      </p>
                    )}
                  </div>

                  <div className="mt-6 flex justify-center space-x-3">
                    <button
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          if (!token) {
                            toast.error('Please log in to download files');
                            return;
                          }

                          const response = await fetch(`${API_BASE_URL}/upload/download/${leadData?.businessCardImage}`, {
                            method: 'GET',
                            headers: {
                              'Authorization': `Bearer ${token}`
                            }
                          });

                          if (!response.ok) {
                            throw new Error('Download failed');
                          }

                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `business-card-${leadData?.firstName}-${leadData?.lastName}.jpg`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);

                          toast.success('Download started successfully');
                        } catch (error) {
                          console.error('Download error:', error);
                          toast.error('Download failed. Please try again.');
                        }
                      }}
                      className="btn-secondary"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download
                    </button>
                    <button
                      onClick={() => setShowBusinessCardModal(false)}
                      className="btn-primary"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Service Record Modal */}
      {showServiceRecordModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 backdrop-blur-sm" 
              onClick={() => setShowServiceRecordModal(false)}
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full border border-gray-200">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileIcon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-xl font-bold text-gray-900">Service Record Details</h3>
                      <p className="text-sm text-gray-500">Full automated history and logs</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowServiceRecordModal(false)}
                    className="p-2 text-gray-400 hover:text-gray-500 rounded-full hover:bg-gray-100 transition-all"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-8 max-h-[70vh] overflow-y-auto">
                <div 
                  className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ 
                    __html: selectedServiceRecord ? DOMPurify.sanitize(selectedServiceRecord, {
                      ALLOWED_TAGS: ['div', 'span', 'h1', 'h2', 'h3', 'p', 'br', 'a', 'b', 'i', 'strong', 'em', 'audio', 'source', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'ul', 'ol', 'li'],
                      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'src', 'controls', 'type', 'width', 'height']
                    }) : '' 
                  }}
                />
              </div>
              <div className="bg-white px-6 py-4 border-t border-gray-100 flex justify-end space-x-3">
                {hasAutoRepairPermission && (
                  <button
                    onClick={() => {
                      const printWindow = window.open('', '_blank');
                      printWindow.document.write(`
                        <html>
                          <head>
                            <title>Service Record</title>
                            <style>
                              body { font-family: sans-serif; padding: 40px; }
                              @media print { body { padding: 0; } }
                            </style>
                          </head>
                          <body>
                            ${DOMPurify.sanitize(selectedServiceRecord)}
                            <script>window.onload = function() { window.print(); window.onafterprint = function() { window.close(); }; }</script>
                          </body>
                        </html>
                      `);
                      printWindow.document.close();
                    }}
                    className="btn-secondary flex items-center"
                  >
                    <Printer size={16} className="mr-2" />
                    Print Record
                  </button>
                )}
                <button
                  onClick={() => setShowServiceRecordModal(false)}
                  className="btn-primary"
                >
                  Close View
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Service Record Modal */}
      {showCreateServiceRecordModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 backdrop-blur-sm" onClick={() => setShowCreateServiceRecordModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full border border-gray-200">
              <div className="bg-white px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white mr-4">
                    <FileIcon size={20} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Create New Service Record</h3>
                    <p className="text-sm text-gray-500">Generate a professional vehicle history document</p>
                  </div>
                </div>
                <button onClick={() => setShowCreateServiceRecordModal(false)} className="text-gray-400 hover:text-gray-500 p-2 rounded-full hover:bg-gray-100"><X size={24} /></button>
              </div>

              <div className="px-8 py-6 max-h-[70vh] overflow-y-auto bg-gray-50">
                <div className="space-y-6">
                  {/* Vehicle Section */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Vehicle Mark / Model <span className="text-red-500">*</span></label>
                      <input 
                        type="text" 
                        value={serviceRecordForm.vehicleModel}
                        onChange={(e) => setServiceRecordForm({...serviceRecordForm, vehicleModel: e.target.value})}
                        placeholder="e.g. Tesla Model 3" 
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Odometer</label>
                      <input 
                        type="text" 
                        value={serviceRecordForm.odometer}
                        onChange={(e) => {
                          const formatted = formatOdometerInput(e.target.value);
                          setServiceRecordForm({...serviceRecordForm, odometer: formatted});
                        }}
                        placeholder="e.g. 42,500" 
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Extra Info</label>
                      <input 
                        type="text" 
                        value={serviceRecordForm.extraInfo}
                        onChange={(e) => setServiceRecordForm({...serviceRecordForm, extraInfo: e.target.value})}
                        placeholder="e.g. FSD Enabled" 
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      />
                    </div>
                  </div>

                  {/* Maintenance Items Section */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-bold text-gray-700 uppercase">Performed Maintenance</label>
                      <button 
                        onClick={() => setServiceRecordForm({
                          ...serviceRecordForm, 
                          items: [...serviceRecordForm.items, { description: '', category: '', status: 'PASSED' }]
                        })}
                        className="text-xs text-blue-600 font-bold hover:text-blue-700 decoration-dotted underline"
                      >
                        + Add New Item
                      </button>
                    </div>
                    <div className="space-y-3">
                      {serviceRecordForm.items.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-2 bg-white p-3 rounded-lg border border-gray-200 shadow-sm relative">
                          <div className="flex-1">
                            <input 
                              type="text" 
                              value={item.description}
                              onChange={(e) => {
                                const newItems = [...serviceRecordForm.items];
                                newItems[idx].description = e.target.value;
                                setServiceRecordForm({...serviceRecordForm, items: newItems});
                              }}
                              placeholder="Maintenance Description..." 
                              className="w-full text-sm border-0 focus:ring-0 p-0 mb-1 font-medium placeholder-gray-400"
                            />
                            <div className="flex gap-4">
                              <input 
                                type="text" 
                                value={item.category}
                                onChange={(e) => {
                                  const newItems = [...serviceRecordForm.items];
                                  newItems[idx].category = e.target.value;
                                  setServiceRecordForm({...serviceRecordForm, items: newItems});
                                }}
                                placeholder="Category (e.g. Sensors, Battery)" 
                                className="text-xs border-0 focus:ring-0 p-0 text-gray-500 w-1/3"
                              />
                              <div className="flex items-center text-xs text-gray-400 w-1/4">
                                <span className="mr-1">$</span>
                                <input 
                                  type="text" 
                                  value={item.price}
                                  onChange={(e) => {
                                    const formatted = formatPriceInput(e.target.value);
                                    const newItems = [...serviceRecordForm.items];
                                    newItems[idx].price = formatted;
                                    setServiceRecordForm({...serviceRecordForm, items: newItems});
                                  }}
                                  placeholder="0.00" 
                                  className="border-0 focus:ring-0 p-0 text-blue-600 font-bold w-full"
                                />
                              </div>
                              <select 
                                value={item.status}
                                onChange={(e) => {
                                  const newItems = [...serviceRecordForm.items];
                                  newItems[idx].status = e.target.value;
                                  setServiceRecordForm({...serviceRecordForm, items: newItems});
                                }}
                                className="text-xs border-0 focus:ring-0 p-0 text-blue-600 font-bold"
                              >
                                <option value="PASSED">PASSED</option>
                                <option value="UPDATED">UPDATED</option>
                                <option value="CHECKED">CHECKED</option>
                                <option value="WARNING">WARNING</option>
                              </select>
                            </div>
                          </div>
                          {serviceRecordForm.items.length > 1 && (
                            <button 
                              onClick={() => {
                                const newItems = serviceRecordForm.items.filter((_, i) => i !== idx);
                                setServiceRecordForm({...serviceRecordForm, items: newItems});
                              }}
                              className="text-gray-300 hover:text-red-500"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Remarks Section */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-2">Technician Remarks</label>
                    <textarea 
                      rows={3} 
                      value={serviceRecordForm.remarks}
                      onChange={(e) => setServiceRecordForm({...serviceRecordForm, remarks: e.target.value})}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none transition-all text-sm"
                      placeholder="Enter summarized findings and recommendations..."
                    />
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-white border-t border-gray-100 flex justify-end space-x-3">
                <button onClick={() => setShowCreateServiceRecordModal(false)} className="btn-secondary">Cancel</button>
                <button 
                  onClick={() => {
                    // Validasyon
                    if (!serviceRecordForm.vehicleModel.trim()) {
                      toast.error('Vehicle Mark / Model is required');
                      return;
                    }

                    // HTML Şablonunu Oluştur
                    const refNo = `#2026-${leadData.lastName?.substring(0,2).toUpperCase() || 'SR'}-${Math.floor(1000 + Math.random() * 9000)}`;
                    const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
                    const leadName = formatName(leadData.firstName, leadData.lastName);
                    const leadLocation = leadData.city || 'Los Angeles, California';

                    const totalAmount = serviceRecordForm.items.reduce((sum, item) => {
                      const numericPrice = parseFloat(String(item.price).replace(/,/g, '')) || 0;
                      return sum + numericPrice;
                    }, 0);

                    const rowsHtml = serviceRecordForm.items.map(item => {
                      const numericPrice = parseFloat(String(item.price).replace(/,/g, '')) || 0;
                      return `
                        <tr style="border-bottom: 1px solid #f0f0f0;">
                            <td style="padding: 15px 0;">${item.description || 'Routine analysis'}</td>
                            <td style="padding: 15px 0; color: #777;">${item.category || 'Maintenance'}</td>
                            <td style="padding: 15px 0;">${item.price ? '$' + numericPrice.toLocaleString('en-US', { minimumFractionDigits: 2 }) : '-'}</td>
                            <td style="padding: 15px 0; text-align: right; color: ${item.status === 'WARNING' ? '#d32f2f' : '#2e7d32'}; font-weight: bold;">${item.status}</td>
                        </tr>
                      `;
                    }).join('');

                    const finalHtml = `
<div class="autolab-service-record-container" style="width: 100%; max-width: 800px; padding: 40px; margin: auto; box-sizing: border-box; font-family: 'Segoe UI', Arial, sans-serif; color: #222; background: #fff; line-height: 1.5; border: 1px solid #eee; border-radius: 12px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 40px;">
        <div>
            <h1 style="margin: 0; font-size: 26px; color: #111; font-weight: 800; letter-spacing: -0.5px;">VEHICLE SERVICE RECORD</h1>
            <p style="margin: 2px 0; color: #666; font-size: 13px;">${COMPANY_NAME.toUpperCase()} • SERVICE HUB</p>
        </div>
        <div style="text-align: right; font-size: 11px; color: #888; text-transform: uppercase;">
            <strong>Ref No:</strong> ${refNo}<br>
            <strong>Date:</strong> ${dateStr}
        </div>
    </div>
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-bottom: 40px;">
        <div style="border-top: 1px solid #eee; padding-top: 15px;">
            <div style="font-size: 10px; color: #aaa; text-transform: uppercase; margin-bottom: 5px; font-weight: bold;">Owner Information</div>
            <div style="font-size: 15px;"><strong>${leadName}</strong></div>
            <div style="font-size: 13px; color: #555;">${leadLocation}</div>
        </div>
        <div style="border-top: 1px solid #eee; padding-top: 15px;">
            <div style="font-size: 10px; color: #aaa; text-transform: uppercase; margin-bottom: 5px; font-weight: bold;">Vehicle Details</div>
            <div style="font-size: 15px;"><strong>${serviceRecordForm.vehicleModel || 'Tesla Model 3'}</strong></div>
            <div style="font-size: 13px; color: #555;">${serviceRecordForm.odometer || '42,500 mi'}${serviceRecordForm.extraInfo ? ` &bull; ${serviceRecordForm.extraInfo}` : ''}</div>
        </div>
    </div>
    <div style="margin-bottom: 40px;">
        <table style="width: 100%; border-collapse: collapse;">
            <thead>
                <tr style="text-align: left; font-size: 11px; color: #aaa; border-bottom: 1.5px solid #111; text-transform: uppercase;">
                    <th style="padding-bottom: 10px;">Description</th>
                    <th style="padding-bottom: 10px; width: 120px;">Category</th>
                    <th style="padding-bottom: 10px; width: 100px;">Price</th>
                    <th style="padding-bottom: 10px; width: 80px; text-align: right;">Status</th>
                </tr>
            </thead>
            <tbody style="font-size: 14px;">
                ${rowsHtml}
                <tr style="border-top: 2px solid #111; font-weight: bold;">
                    <td colspan="2" style="padding: 15px 0;">TOTAL SERVICE AMOUNT</td>
                    <td style="padding: 15px 0;">$${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td></td>
                </tr>
            </tbody>
        </table>
    </div>
    <div style="margin-bottom: 40px;">
        <div style="font-size: 10px; color: #aaa; text-transform: uppercase; margin-bottom: 10px; font-weight: bold;">Technician Remarks</div>
        <p style="margin: 0; font-size: 13px; color: #444; line-height: 1.7;">${serviceRecordForm.remarks || 'Hardware communication within nominal limits. Optimized performance for current weather conditions.'}</p>
    </div>
    <div style="margin-top: 30px; display: flex; justify-content: space-between; align-items: flex-end; padding-top: 30px; border-top: 1px dashed #eee;">
        <div>
            <div style="font-size: 16px; font-weight: bold; color: #2e7d32; margin-bottom: 5px;">Certified System Check</div>
            <div style="font-size: 10px; color: #aaa;">Digital Verification ID: SR-${Math.floor(Date.now() / 1000)}</div>
        </div>
        <div style="text-align: center;">
            <div style="font-family: 'Courier New', Courier, monospace; font-size: 14px; color: #555;"><i>${COMPANY_NAME} Authorized</i></div>
            <div style="border-top: 1px solid #333; width: 180px; margin-top: 5px;"></div>
        </div>
    </div>
</div>`;
                    // Notu kaydet
                    addNoteMutation.mutate({ content: finalHtml, customFields: null });
                    setShowCreateServiceRecordModal(false);
                    setServiceRecordForm({ vehicleModel: '', odometer: '', extraInfo: '', remarks: '', items: [{ description: '', category: '', status: 'PASSED', price: '' }] });
                    toast.success('Professional Service Record generated and added to notes!');
                  }} 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-bold transition-all shadow-lg shadow-blue-200"
                >
                  Generate Record
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDetail; 